import { CarrierVisitDirection } from '@planning/app/api'
import { IRailVisitItem } from '@planning/rt-stores/railVisit/RailVisitItem'
import { EmptyIndicator } from '@tom-ui/ui'
import { computed, makeObservable, observable, runInAction } from 'mobx'
import { RailVisitTableOrderItemDto } from '../../tableConfigs/useRailVisitTableOrderItemColumns'

export class RailVisitTableViewStore {
  railVisitItem: IRailVisitItem | null = null
  direction: CarrierVisitDirection = CarrierVisitDirection.Inbound

  constructor(railVisitItem: IRailVisitItem, direction: CarrierVisitDirection) {
    runInAction(() => {
      this.railVisitItem = railVisitItem
      this.direction = direction
    })

    makeObservable(this, {
      railVisitItem: observable,
      direction: observable,

      tableData: computed,
      usedRailcarIds: computed,
      ordersByDirection: computed,
    })
  }

  get tableData(): RailVisitTableOrderItemDto[] {
    if (!this.railVisitItem) return []

    return this.ordersByDirection.sort((a, b) => a.sequence - b.sequence)
  }

  get usedRailcarIds(): number[] {
    if (!this.railVisitItem) return []

    const allPositions = this.railVisitItem.railcarTrackPositions || []

    const ids = allPositions.map(pos => pos.data.railcarId).filter((id): id is number => !!id)

    return [...new Set(ids)]
  }

  get ordersByDirection(): RailVisitTableOrderItemDto[] {
    const rawOrders =
      this.direction === CarrierVisitDirection.Inbound
        ? this.railVisitItem?.discharge.containerOrders
        : this.railVisitItem?.load.containerOrders

    return (rawOrders || []).map(order => {
      const d = order.data
      const rtp = order.railcarTrackPosition
      return {
        id: order.id,
        railTrack: rtp?.data.railTrackName || d.railTrackName || EmptyIndicator,
        sequence: rtp?.data.railcarSequenceNumber || d.railcarSequenceNumber || 0,
        railcarNo: rtp?.data.railcarName || d.waggon || EmptyIndicator,

        containerNo: d.containerNumber || EmptyIndicator,
        status: order.containerJourney?.status ?? 'Expected',
        approved: d.isApproved ? 'yes' : 'no',
        originalOrder: order,

        isoCode: d.containerIsoCode?.toUpperCase() || EmptyIndicator,
        cargoStatus: d.isEmpty ? 'Empty' : 'Full',
        weight: d.grossWeight || 0,
        vgm: d.vgm || 0,
        oog: d.isOOG ? 'Yes' : 'No',
        cooling: order.containerJourney?.data.coolingOrderId ? 'Yes' : 'No',
        imoClass:
          Array.isArray(d.imoClasses) && d.imoClasses.length > 0 ? d.imoClasses.join(', ') : 'No',
        seals:
          Array.isArray(d.seals) && d.seals.length > 0
            ? d.seals
                .map(s => s?.number ?? '')
                .filter(Boolean)
                .join(', ')
            : d.hasSeals
              ? 'Yes'
              : 'No',

        pol: d.portOfLoading || EmptyIndicator,
        pod: d.portOfDischarge || EmptyIndicator,
        shippingLine: d.shippingLine || EmptyIndicator,
        consignee: d.consignee || EmptyIndicator,
        customer: d.customerName || EmptyIndicator,
        operator: d.operator || EmptyIndicator,

        numericReleaseReference: d.numericReleaseReference || undefined,
        nonNumericReleaseReference: d.nonNumericReleaseReference || undefined,
      }
    })
  }
}
